import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Copy } from "lucide-react";
import { WhatsAppIcon } from "@/components/icons/whatsapp";
import { useToast } from "@/hooks/use-toast";
import type { Funcionario, Tarefa, Atividade } from "@shared/schema";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { formatSetores } from "@/lib/setores";

interface CompartilharPlanoDialogProps {
  open: boolean;
  onClose: () => void;
  data: string;
  funcionarios: Funcionario[];
  tarefas: Tarefa[];
  atividades: Atividade[];
  setores: { id: string; nome: string }[];
}

// Marcadores BMP — sobrevivem ao WhatsApp Desktop (sem emojis astral-plane)
const MARK_FUNC = "▸"; // U+25B8
const MARK_TASK = "•"; // U+2022

export function CompartilharPlanoDialog({ open, onClose, data, funcionarios, tarefas, atividades, setores }: CompartilharPlanoDialogProps) {
  const { toast } = useToast();
  const atividadeMap = new Map(atividades.map(a => [a.id, a]));

  const dataFormatada = format(new Date(data + "T00:00:00"), "dd/MM/yyyy", { locale: ptBR });

  const linhas: string[] = [`Plano do Campo — ${dataFormatada}`, ""];

  const funcsComTarefas = funcionarios.filter(f => tarefas.some(t => t.funcionarioId === f.id));

  for (const func of funcsComTarefas) {
    linhas.push(`${MARK_FUNC} ${func.apelido ?? func.nome}`);
    const tarefasFunc = tarefas.filter(t => t.funcionarioId === func.id);
    for (const t of tarefasFunc) {
      const ativ = atividadeMap.get(t.atividadeId ?? "");
      const desc = t.status === "FALTOU" ? "Faltou" : (ativ?.nome ?? "Tarefa");
      const ids = t.setorIds?.length ? t.setorIds : (t.setorId ? [t.setorId] : []);
      const setorLabel = t.status === "FALTOU" ? "" : formatSetores(ids, setores);
      const obs = t.observacoes ? ` — ${t.observacoes}` : "";
      linhas.push(`  ${MARK_TASK} ${desc}${setorLabel ? ` (${setorLabel})` : ""}${obs}`);
    }
    linhas.push("");
  }

  linhas.push(`Total: ${funcsComTarefas.length} funcionário${funcsComTarefas.length !== 1 ? "s" : ""}`);
  const texto = linhas.join("\n");

  const handleCopy = async () => {
    await navigator.clipboard.writeText(texto);
    toast({ title: "Copiado!", description: "Texto copiado para a área de transferência." });
  };

  const handleWhatsapp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(texto)}`, "_blank");
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Compartilhar Plano do Campo</DialogTitle>
        </DialogHeader>
        <Textarea value={texto} readOnly rows={12} className="font-mono text-sm" />
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleCopy}>
            <Copy className="h-4 w-4 mr-2" />
            Copiar
          </Button>
          <Button onClick={handleWhatsapp}>
            <WhatsAppIcon className="h-4 w-4 mr-2" />
            WhatsApp
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
